import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import "../styles/agua-potable.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_LABELS = {
  ALJ: "Aljibe / Cisterna",
  TIN: "Tinaco",
  PIL: "Pila",
  POZ: "Pozo",
  OTR: "Otro",
};

const CAT_LABELS = {
  EDU: "Educativo",
  PUB: "Público",
  DOM: "Doméstico",
  PAN: "Panteón",
  GEN: "General",
  REL: "Religioso",
  OFI: "Oficial",
};

const PRIORIDAD_COLORS = {
  ALTA: "#DC2626",
  MEDIA: "#D97706",
  BAJA: "#0891B2",
};

export default function AguaPotablePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fTipo, setFTipo] = useState("");
  const [fPrioridad, setFPrioridad] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fQ, setFQ] = useState("");

  // Modal GPS
  const [modalPunto, setModalPunto] = useState(null);   // {codigo, nombre_oficial, gps_lat, gps_lng}
  const [modalLoc, setModalLoc] = useState(false);      // obteniendo ubicación
  const [modalLat, setModalLat] = useState("");
  const [modalLng, setModalLng] = useState("");
  const [modalSaving, setModalSaving] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fTipo) params.set("tipo", fTipo);
      if (fPrioridad) params.set("prioridad", fPrioridad);
      if (fEstado) params.set("estado", fEstado);
      if (fQ.trim()) params.set("q", fQ.trim());
      const r = await axios.get(`${API}/admin/agua-potable/?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar el catálogo");
    } finally {
      setLoading(false);
    }
  }, [fTipo, fPrioridad, fEstado, fQ]);

  useEffect(() => { cargar(); }, [cargar]);

  const items = data?.items || [];

  const onAgregarGPS = (punto) => {
    setModalPunto(punto);
    setModalLat(punto.gps_lat != null ? String(punto.gps_lat) : "");
    setModalLng(punto.gps_lng != null ? String(punto.gps_lng) : "");
  };

  const cerrarModal = () => {
    if (modalSaving || modalLoc) return;
    setModalPunto(null);
    setModalLat("");
    setModalLng("");
  };

  const usarUbicacionActual = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setModalLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setModalLat(lat.toFixed(6));
        setModalLng(lng.toFixed(6));
        setModalLoc(false);
        toast.success(`Ubicación obtenida (±${Math.round(pos.coords.accuracy || 0)} m)`);
      },
      (err) => {
        setModalLoc(false);
        const map = {
          1: "Permiso denegado. Activa la ubicación en el navegador.",
          2: "No se pudo obtener ubicación (sin señal GPS).",
          3: "Tiempo de espera agotado. Intenta de nuevo cerca de una ventana.",
        };
        toast.error(map[err.code] || "Error al obtener ubicación");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const guardarModal = async () => {
    const lat = parseFloat(modalLat);
    const lng = parseFloat(modalLng);
    if (isNaN(lat) || isNaN(lng)) {
      toast.error("GPS inválido. Captura ambos valores.");
      return;
    }
    if (lat < 28 || lat > 30 || lng > -105 || lng < -107) {
      const ok = window.confirm(
        `Las coordenadas (${lat}, ${lng}) parecen estar fuera de Chihuahua.\n¿Continuar de todos modos?`
      );
      if (!ok) return;
    }
    setModalSaving(true);
    try {
      await axios.put(`${API}/admin/agua-potable/${encodeURIComponent(modalPunto.codigo)}`, {
        gps_lat: lat,
        gps_lng: lng,
      });
      toast.success(`GPS guardado: ${modalPunto.codigo}`);
      setModalPunto(null);
      setModalLat("");
      setModalLng("");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar GPS");
    } finally {
      setModalSaving(false);
    }
  };

  const onLimpiar = () => {
    setFTipo(""); setFPrioridad(""); setFEstado(""); setFQ("");
  };

  return (
    <div className="hub-bg ap-root" data-testid="page-agua-potable">
      <header className="topnav">
        <div className="brand">
          <button onClick={() => navigate("/hub")} className="iconbtn mr-1" aria-label="Atrás" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left"/>
          </button>
          <div className="logo" style={{ background: "#0EA5E9" }}>
            <i className="fa-solid fa-droplet"/>
          </div>
          <div>
            <div className="title">Rutas de Agua Potable</div>
            <div className="sub">DMU 614 · Catálogo Maestro · {data?.total_global || 0} puntos</div>
          </div>
        </div>
        <div className="actions">
          <div className="hidden sm:block text-right mr-2">
            <div className="text-sm font-semibold leading-tight">{user?.nombre}</div>
            <div className="text-xs text-slate-500">{user?.rol}</div>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout-ap" style={{ padding: "0.5rem 0.9rem" }}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 ap-main">
        {/* KPIs */}
        <section className="ap-kpis" data-testid="ap-kpis">
          <div className="ap-kpi">
            <div className="ap-kpi-icon" style={{ background: "#0EA5E9" }}><i className="fa-solid fa-list"/></div>
            <div>
              <div className="ap-kpi-num">{data?.total_global || 0}</div>
              <div className="ap-kpi-lbl">Total puntos</div>
            </div>
          </div>
          <div className="ap-kpi">
            <div className="ap-kpi-icon" style={{ background: "#10B981" }}><i className="fa-solid fa-location-dot"/></div>
            <div>
              <div className="ap-kpi-num">{data?.con_gps || 0}</div>
              <div className="ap-kpi-lbl">Con GPS</div>
            </div>
          </div>
          <div className="ap-kpi">
            <div className="ap-kpi-icon" style={{ background: "#F59E0B" }}><i className="fa-solid fa-hourglass-half"/></div>
            <div>
              <div className="ap-kpi-num">{data?.pendientes_gps || 0}</div>
              <div className="ap-kpi-lbl">Pendientes GPS</div>
            </div>
          </div>
        </section>

        {/* Filtros */}
        <section className="ap-filtros card-base" data-testid="ap-filtros">
          <div className="ap-filtros-grid">
            <label className="ap-field">
              <span>Tipo</span>
              <select value={fTipo} onChange={e => setFTipo(e.target.value)} data-testid="f-tipo">
                <option value="">— Todos —</option>
                <option value="ALJ">ALJ · Aljibe</option>
                <option value="TIN">TIN · Tinaco</option>
                <option value="PIL">PIL · Pila</option>
                <option value="POZ">POZ · Pozo</option>
                <option value="OTR">OTR · Otro</option>
              </select>
            </label>
            <label className="ap-field">
              <span>Prioridad</span>
              <select value={fPrioridad} onChange={e => setFPrioridad(e.target.value)} data-testid="f-prioridad">
                <option value="">— Todas —</option>
                <option value="ALTA">Alta</option>
                <option value="MEDIA">Media</option>
                <option value="BAJA">Baja</option>
              </select>
            </label>
            <label className="ap-field">
              <span>Estado</span>
              <select value={fEstado} onChange={e => setFEstado(e.target.value)} data-testid="f-estado">
                <option value="">— Todos —</option>
                <option value="PENDIENTE GPS">Pendiente GPS</option>
                <option value="CAPTURADO">Capturado</option>
              </select>
            </label>
            <label className="ap-field ap-field-wide">
              <span>Buscar</span>
              <input
                type="text"
                value={fQ}
                onChange={e => setFQ(e.target.value)}
                placeholder="Código o nombre…"
                data-testid="f-q"
              />
            </label>
            <button className="btn-ghost ap-btn-limpiar" onClick={onLimpiar} data-testid="btn-limpiar">
              <i className="fa-solid fa-eraser mr-1"/>Limpiar
            </button>
          </div>
        </section>

        {/* Lista */}
        <section className="ap-lista" data-testid="ap-lista">
          <div className="ap-lista-head">
            <strong>{loading ? "Cargando…" : `${items.length} resultados`}</strong>
            <span className="text-xs text-slate-500">Ordenados por código</span>
          </div>

          {items.length === 0 && !loading && (
            <div className="ap-vacio">Sin resultados con los filtros actuales.</div>
          )}

          <ul className="ap-cards">
            {items.map(p => (
              <li key={p.codigo} className="ap-card" data-testid={`ap-card-${p.codigo}`}>
                <div className="ap-card-head">
                  <div className="ap-card-codigo">{p.codigo}</div>
                  <div className="ap-card-tags">
                    <span className="ap-tag ap-tag-tipo">{p.tipo || "—"}</span>
                    <span className="ap-tag ap-tag-cat">{p.cat || "—"}</span>
                    {p.prioridad && (
                      <span className="ap-tag" style={{ background: PRIORIDAD_COLORS[p.prioridad] || "#475569", color: "#fff" }}>
                        {p.prioridad}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ap-card-nombre">{p.nombre_oficial || "Sin nombre"}</div>
                <div className="ap-card-meta">
                  {p.frecuencia && <span><i className="fa-solid fa-calendar-week mr-1"/>{p.frecuencia}</span>}
                  {p.servicios != null && <span><i className="fa-solid fa-truck-droplet mr-1"/>{p.servicios} servicios</span>}
                  <span className="ap-card-tipo-label">{TIPO_LABELS[p.tipo] || ""} {p.cat ? `· ${CAT_LABELS[p.cat] || p.cat}` : ""}</span>
                </div>
                <div className="ap-card-gps">
                  {p.gps_lat && p.gps_lng ? (
                    <>
                      <span className="ap-gps-ok">
                        <i className="fa-solid fa-circle-check mr-1"/>
                        GPS: {Number(p.gps_lat).toFixed(6)}, {Number(p.gps_lng).toFixed(6)}
                      </span>
                      <a
                        href={`https://www.google.com/maps?q=${p.gps_lat},${p.gps_lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ap-link-mapa"
                        data-testid={`ap-ver-mapa-${p.codigo}`}
                      >
                        <i className="fa-solid fa-map-location-dot mr-1"/>Ver en mapa
                      </a>
                      <button
                        className="btn-ghost ap-btn-gps"
                        onClick={() => onAgregarGPS(p)}
                        data-testid={`ap-editar-gps-${p.codigo}`}
                      >
                        <i className="fa-solid fa-pen mr-1"/>Editar GPS
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="ap-gps-pendiente">
                        <i className="fa-solid fa-triangle-exclamation mr-1"/>{p.estado || "PENDIENTE GPS"}
                      </span>
                      <button
                        className="btn-primary ap-btn-gps"
                        onClick={() => onAgregarGPS(p)}
                        data-testid={`ap-agregar-gps-${p.codigo}`}
                      >
                        <i className="fa-solid fa-location-crosshairs mr-1"/>Capturar GPS
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="text-xs text-slate-400 text-center pb-6">
          DMU 614 · Catálogo Maestro · Lectura: admin + supervisor_general_pipas · Edición: admin.
        </section>
      </main>

      {/* ── MODAL CAPTURAR GPS ────────────────────────────── */}
      {modalPunto && (
        <div className="ap-modal-bg" onClick={cerrarModal} data-testid="ap-modal-bg">
          <div className="ap-modal" onClick={e => e.stopPropagation()} data-testid="ap-modal">
            <header className="ap-modal-head">
              <div>
                <div className="ap-modal-codigo">{modalPunto.codigo}</div>
                <div className="ap-modal-nombre">{modalPunto.nombre_oficial}</div>
              </div>
              <button
                className="ap-modal-close"
                onClick={cerrarModal}
                disabled={modalSaving || modalLoc}
                aria-label="Cerrar"
                data-testid="ap-modal-close"
              >
                <i className="fa-solid fa-xmark"/>
              </button>
            </header>

            <div className="ap-modal-body">
              <button
                className="ap-modal-btn-loc"
                onClick={usarUbicacionActual}
                disabled={modalLoc || modalSaving}
                data-testid="ap-modal-usar-ubicacion"
              >
                {modalLoc ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"/>
                    Obteniendo ubicación…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-location-crosshairs mr-2"/>
                    Usar mi ubicación actual
                  </>
                )}
              </button>

              <div className="ap-modal-divisor">
                <span>o capturar manualmente</span>
              </div>

              <div className="ap-modal-row">
                <label className="ap-field">
                  <span>Latitud</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={modalLat}
                    onChange={e => setModalLat(e.target.value)}
                    placeholder="28.6353"
                    disabled={modalLoc || modalSaving}
                    data-testid="ap-modal-lat"
                  />
                </label>
                <label className="ap-field">
                  <span>Longitud</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={modalLng}
                    onChange={e => setModalLng(e.target.value)}
                    placeholder="-106.0889"
                    disabled={modalLoc || modalSaving}
                    data-testid="ap-modal-lng"
                  />
                </label>
              </div>

              {modalLat && modalLng && !isNaN(parseFloat(modalLat)) && !isNaN(parseFloat(modalLng)) && (
                <a
                  href={`https://www.google.com/maps?q=${parseFloat(modalLat)},${parseFloat(modalLng)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ap-modal-preview"
                  data-testid="ap-modal-preview-mapa"
                >
                  <i className="fa-solid fa-map-location-dot mr-1"/>
                  Ver ubicación en Google Maps
                </a>
              )}
            </div>

            <footer className="ap-modal-foot">
              <button
                className="btn-ghost"
                onClick={cerrarModal}
                disabled={modalSaving || modalLoc}
                data-testid="ap-modal-cancelar"
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={guardarModal}
                disabled={modalSaving || modalLoc || !modalLat || !modalLng}
                data-testid="ap-modal-guardar"
              >
                {modalSaving ? (
                  <><i className="fa-solid fa-spinner fa-spin mr-1"/>Guardando…</>
                ) : (
                  <><i className="fa-solid fa-floppy-disk mr-1"/>Guardar GPS</>
                )}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
